// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-nocheck
import { Project, ProjectRoles } from '../../models/project';
import { Investments } from '../../components/FinancesByFy/FinancesByFyProps';
import { trimDate } from './utils';
import { CommentTypesEnum } from '../../models/selectOptionsModels';
import { formatToOriginal, mapInvestmentsToOriginal } from '../utils/utils';

export type PayloadType = {
  id: string;
  etrUnitId: string;
  projectName: string;
  projectStartDate: string; // format: YYYY-MM-DD
  projectCompletionDate: string; // format: YYYY-MM-DD
  unitType: string; // this seems to be a fixed value
  programmeName: string; // this seems to be a fixed value
  projectStatus: string; // this seems to be a fixed value
  projectStatusDate: string; // format: YYYY-MM-DD
  statusComments: string;
  benefitsAndRisk: string;
  additionalProjectInformation: string;
  targetProjectEndDate: string; // format: YYYY-MM-DD
  targetProjectStartDate: string; // format: YYYY-MM-DD
  mergedWithReferenceId: string;
  unifierId: string;
  totalProjectInvestment: number;
  propertyInvestment: number;
  retailInvestment: number;
  landCost: number;
  isep: boolean;
  solutionDescriptionAndCost: string;
  etrDetails: {
    establishmentType: string; // this seems to be a fixed value
    etrProjectPrioritization: string; // this seems to be a fixed value
    projectClosingDate: string; // format: YYYY-MM-DD
    designStartDate: string; // format: YYYY-MM-DD
    constructionStartDate: string; // format: YYYY-MM-DD
    internalAnnouncementDate: string; // format: YYYY-MM-DD
    externalAnnouncementDate: string; // format: YYYY-MM-DD
    handoverToLandlordDate: string; // format: YYYY-MM-DD
    finalDeedDate: string; // format: YYYY-MM-DD
    grossUnitArea: number;
    etrCommentEntities: {
      commentType: string; // this seems to be a fixed value
      commentText: string;
      whenCommented: string; // format: YYYY-MM-DDTHH:mm:ss.sssZ
    }[];
  };
  businessUnit: {
    businessUnitType: string;
    businessUnitId: string;
    businessUnitNumber: string;
  };
  capacity: {
    automationLevel: string; // this seems to be a fixed value
    stockedRangeAdjustment: number;
    storageCapacityAdjustment: number;
    truckFulfilmentCapacityAdjustment: number;
    parcelFulfilmentCapacityAdjustment: number;
    needForAstroDecommissioning: boolean;
  };
  expansionProject: {
    id: string;
    siteType: string; // this seems to be a fixed value
    transactionType: string; // this seems to be a fixed value
    leasePeriod: string;
    isLeasedFromAnotherIngkaCompany?: boolean;
    siteProgress: string; // this seems to be a fixed value
    siteAgreementSignedDate: string; // format: YYYY-MM-DD
    siteAccessibleDate: string; // format: YYYY-MM-DD
    landSize: number;
    grossBuildingArea: number;
    realEstateClusterResponsible: string;
    siteStatusDetails: string;
    countryProjectResponsible: string;
    confidential: boolean;
    etrProjectId: string;
    siteLocation: SiteLocationType;
  };
  projectResponsibleList: {
    projectResponsibleRole: string; // this seems to be a fixed value
    projectResponsiblePerson: string;
  }[];
  expansionProjectEditors: {
    name: string;
    email: string;
    expansionUserRole: string;
  }[];
  investments: {
    id: string;
    fiscalYear: string;
    landCost: number;
    propertyInvestment: number;
    retailInvestment: number;
    retailCost: number;
  }[];
  solutions: {
    id: string;
    solutionCategory: string;
    solutionPurpose: string; // this seems to be a fixed value
    solutionType: string;
    solutionDescription: string;
    etrSolutionId: string;
  }[];
  mtoolId: string;
};

export type ApprovedCRPPayloadType = {
  moveQualifiedToDraft: boolean;
  projects: string[];
};

export type SiteLocationType = {
  id: string;
  countryCode: string;
  countryName: string;
  city: string;
  state: string;
  locationName: string;
  address: string;
};

export type SiteLocationBuType = SiteLocationType & {
  businessUnitId: [string];
};

const mapInvestments = (investments: Investments[]) =>
  mapInvestmentsToOriginal(
    investments
      ?.map((investment) => investment.BUDGET)
      ?.filter((investment) => !!investment?.fiscalYear),
  );

const mapFinancialSummaries = (investments: Investments[]) =>
  mapInvestmentsToOriginal(
    investments?.reduce((acc, investment) => {
      const copy = [...acc];
      if (!investment?.fiscalYear) return copy;
      copy.push({
        financialCategory: 'RELEASED_APPROVED_BUDGET',
        ...investment.RELEASED_APPROVED_BUDGET,
      });
      copy.push({
        financialCategory: 'FORECAST',
        ...investment.FORECAST,
      });
      return copy;
    }, []),
  );

export const mapProjectForBulkPayload = (
  project: Array<Project & { investments: Investments[] }>,
) => ({
  ...project,
  investments: mapInvestments(project.investments),
  financialSummaries: mapFinancialSummaries(project.investments),
});

const mapProjectCommentForPayload = (comment, type) => {
  return {
    commentType: type,
    commentText: comment,
  };
};

export type ExpansionProjectEditor = {
  name: string;
  email: string;
};

export const mapProjectForPaylaod = (
  project: Project & { investments: Investments[] } & {
    expansionProjectEditors: ExpansionProjectEditor[];
  },
): PayloadType => ({
  id: project.id,
  capacity: {
    stockedRangeAdjustment: project.stockedRangeAdjustment,
    storageCapacityAdjustment: project.storageCapacityAdjustment,
    truckFulfilmentCapacityAdjustment:
      project.truckFulfilmentCapacityAdjustment,
    parcelFulfilmentCapacityAdjustment:
      project.parcelFulfilmentCapacityAdjustment,
    needForAstroDecommissioning: project.needForAstroDecommissioning,
    automationLevel: project.automationLevel,
    replenishmentUnit: project.replenishmentUnit,
  },
  investments: project.investments && mapInvestments(project.investments),
  financialSummaries:
    project.investments && mapFinancialSummaries(project.investments),
  expansionProject: {
    siteType: project?.siteType, // this seems to be a fixed value
    transactionType: project?.transactionType, // this seems to be a fixed value
    leasePeriod:
      project?.isLeasedFromAnotherIngkaCompany === 'true'
        ? null
        : project?.leasePeriod,
    isLeasedFromAnotherIngkaCompany:
      project?.isLeasedFromAnotherIngkaCompany === 'true'
        ? true
        : project?.isLeasedFromAnotherIngkaCompany === 'false'
          ? false
          : null,
    siteProgress: project?.siteProgress, // this seems to be a fixed value
    siteAgreementSignedDate: project?.siteAgreementSignedDate, // format: YYYY-MM-DD
    siteAccessibleDate: project?.siteAccessibleDate, // format: YYYY-MM-DD
    landSize: project?.landSize,
    grossBuildingArea: project?.grossBuildingArea,
    realEstateClusterResponsible: project?.realEstateClusterResponsible,
    countryProjectResponsible: project?.countryProjectResponsible,
    confidential: project?.confidential,
    siteLocation: {
      siteLocationId: project?.siteLocationId,
      countryCode: project?.countryCode,
      city: project?.city,
      state: project?.state,
      locationName: project?.locationName,
      address: project?.address,
    },
  },
  businessUnit: {
    businessUnitId: project?.businessUnitId,
  },
  projectName: project?.projectName,
  projectStartDate: trimDate(project?.projectStartDate), // format: YYYY-MM-DD
  projectCompletionDate: trimDate(project?.projectCompletionDate), // format: YYYY-MM-DD
  unitType: project?.unitType, // this seems to be a fixed value
  unitCategory: project?.unitCategory,
  programmeName: project?.programmeName || null,
  projectStatus: project?.projectStatus, // this seems to be a fixed value
  projectStatusDate: trimDate(project?.projectStatusDate), // format: YYYY-MM-DD
  benefitsAndRisk: project?.benefitsAndRisk,
  additionalProjectInformation: project?.additionalProjectInformation,
  targetProjectEndDate: trimDate(project?.targetProjectEndDate),
  targetProjectStartDate: trimDate(project?.targetProjectStartDate), // format: YYYY-MM-DD
  mergedWithReferenceId: project?.mergedWithReferenceId,
  unifierId: project?.unifierId,
  solutionDescriptionAndCost: project?.solutionDescriptionAndCost,
  lastUpdatedDate: project?.lastUpdatedDate,
  lastUpdatedUser: project?.lastUpdatedUser,
  solutions:
    project?.solutions?.map((solution) => ({
      ...solution,
    })) || [],

  etrDetails: {
    terminationOfFranchiseeAgreement: project.terminationOfFranchiseeAgreement,
    totalNumberOfFullTimeEmployees: project.totalNumberOfFullTimeEmployees,
    totalNumberOfEmployees: project.totalNumberOfEmployees,
    estimatedPeopleImpact: project.estimatedPeopleImpact,
    communicationsAndPcComplexity: project.communicationsAndPcComplexity,
    establishmentType: project?.establishmentType, // this seems to be a fixed value
    etrProjectPrioritization: project?.etrProjectPrioritization,
    projectClosingDate: project?.projectClosingDate, // format: YYYY-MM-DD
    designStartDate: project?.designStartDate, // format: YYYY-MM-DD
    constructionStartDate: project?.constructionStartDate, // format: YYYY-MM-DD
    internalAnnouncementDate: project?.internalAnnouncementDate, // format: YYYY-MM-DD
    externalAnnouncementDate: project?.externalAnnouncementDate, // format: YYYY-MM-DD
    handoverToLandlordDate: project?.handoverToLandlordDate, // format: YYYY-MM-DD
    finalDeedDate: project?.finalDeedDate, // format: YYYY-MM-DD
    grossUnitArea: project?.grossUnitArea,
    newUnitType: project?.newUnitType,
    reasonForClosing: project?.reasonForClosing,
    projectBackgroundAndScope: project?.projectBackgroundAndScope,
    estimatedNetEffert: project?.estimatedNetEffert,
    costAtLeaseEnd:
      project?.isLeasedFromAnotherIngkaCompany === 'true'
        ? null
        : project?.costAtLeaseEnd &&
          formatToOriginal(Number(project?.costAtLeaseEnd)),
    totalLeaseAmount:
      project?.isLeasedFromAnotherIngkaCompany === 'true'
        ? null
        : project?.totalLeaseAmount &&
          formatToOriginal(Number(project?.totalLeaseAmount)),
    etrCommentEntities: [
      mapProjectCommentForPayload(
        project?.statusComments,
        CommentTypesEnum.COUNTRY_PROJECT_STATUS_COMMENTS,
      ),
      mapProjectCommentForPayload(
        project?.projectTimeStatusComment,
        CommentTypesEnum.PROJECT_TIME_STATUS_COMMENTS,
      ),
    ],
  },
  expansionProjectEditors: project?.expansionProjectEditors,
  projectResponsibleList: Object.keys(ProjectRoles)
    .map((role) => ({
      projectResponsibleRole: role,
      projectResponsiblePerson: project[ProjectRoles[role]],
    }))
    .filter((role) => !!role.projectResponsiblePerson),
  partOfCrp: project.partOfCrp,
});
